import type { ReactNode } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import {
  inviteStepActiveClass,
  inviteStepConnectorActiveClass,
  inviteStepLabelActiveClass,
} from './inviteOnboardingUi'

export type InviteOnboardingStepId = 'welcome' | 'acknowledgements' | 'registration'

const STEPS: Array<{ id: InviteOnboardingStepId; label: string; number: number }> = [
  { id: 'welcome', label: 'Welcome', number: 1 },
  { id: 'acknowledgements', label: 'Fees & Obligations', number: 2 },
  { id: 'registration', label: 'Registration', number: 3 },
]

type InviteOnboardingStepperProps = {
  currentStep: InviteOnboardingStepId
}

function stepIndex(id: InviteOnboardingStepId) {
  return STEPS.findIndex((step) => step.id === id)
}

export function InviteOnboardingStepper({ currentStep }: InviteOnboardingStepperProps) {
  const activeIndex = stepIndex(currentStep)

  return (
    <nav aria-label="Invitation acceptance progress" data-testid="invite-onboarding-stepper">
      <ol className="mx-auto flex max-w-3xl items-start justify-between gap-2">
        {STEPS.map((step, index) => {
          const completed = index < activeIndex
          const active = index === activeIndex
          return (
            <li key={step.id} className="relative flex flex-1 flex-col items-center text-center">
              {index < STEPS.length - 1 && (
                <span
                  aria-hidden
                  className={cn(
                    'absolute left-[calc(50%+1.25rem)] right-[calc(-50%+1.25rem)] top-5 h-0.5',
                    completed ? inviteStepConnectorActiveClass : 'bg-slate-200',
                  )}
                />
              )}
              <span
                className={cn(
                  'relative z-10 flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold shadow-sm',
                  completed && 'bg-emerald-500 text-white',
                  active && inviteStepActiveClass,
                  !completed && !active && 'bg-slate-100 text-slate-400',
                )}
                aria-current={active ? 'step' : undefined}
              >
                {completed ? <Check className="h-5 w-5" aria-hidden /> : step.number}
              </span>
              <span
                className={cn(
                  'mt-2 max-w-[8.5rem] text-xs font-semibold sm:text-sm',
                  active ? inviteStepLabelActiveClass : completed ? 'text-emerald-700' : 'text-slate-400',
                )}
              >
                {step.label}
              </span>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

type InviteOnboardingLayoutProps = {
  currentStep: InviteOnboardingStepId
  children: ReactNode
  title?: string
}

export function InviteOnboardingLayout({
  currentStep,
  children,
  title,
}: InviteOnboardingLayoutProps) {
  return (
    <div className="min-h-screen bg-[#f4f7fb]" data-testid="invite-onboarding-layout">
      <div className="mx-auto w-full max-w-6xl px-4 pb-16 pt-8 sm:px-6">
        <InviteOnboardingStepper currentStep={currentStep} />
        {title ? (
          <h1 className="mt-8 text-center text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {title}
          </h1>
        ) : null}
        <div className={cn('mt-6', title ? 'mt-6' : 'mt-8')}>{children}</div>
      </div>
    </div>
  )
}
